import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../lib/utils';
import { useAuthStore } from '../stores';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from './ui/scroll-area';

const NotificationBell = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    refetchInterval: 30000,
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markReadMutation = useMutation({
    mutationFn: async (id) => {
      await fetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => queryClient.invalidateQueries(['notifications'])
  });

  const handleClick = (notif) => {
    if (!notif.is_read) markReadMutation.mutate(notif.id || notif._id);
    if (notif.link) {
      setIsOpen(false);
      navigate(notif.link);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {/* Bouton : Couleur adaptative (Gris foncé en light, Blanc en dark) */}
        <Button variant="ghost" size="icon" className="relative text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/10">
          <Bell className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>
          )}
        </Button>
      </PopoverTrigger>

      {/* FENÊTRE POPOVER : Blanc par défaut, Sombre en dark mode */}
      <PopoverContent
        className="w-80 p-0 mr-0 backdrop-blur-sm 
                   bg-white/95 border-slate-200 text-slate-900 
                   dark:bg-slate-900/95 dark:border-slate-800 dark:text-slate-200"
        align="end"
        sideOffset={10}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-200">
              {unreadCount} nouvelle(s)
            </Badge>
          )}
        </div>

        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Rien à signaler pour le moment 😴
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {notifications.map((n) => (
                <div
                  key={n.id || n._id}
                  onClick={() => handleClick(n)}
                  className={`p-4 cursor-pointer transition-colors flex gap-3 items-start 
                    hover:bg-slate-50 dark:hover:bg-slate-800/50 
                    ${!n.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                >
                  <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${!n.is_read ? 'bg-blue-500' : 'bg-transparent'}`} />
                  <div className="space-y-1">
                    <p className={`text-sm ${!n.is_read ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-700 dark:text-slate-300'}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                      {n.message}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500">
                      {new Date(n.created_at).toLocaleDateString()} à {new Date(n.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;